import type { Specimen } from "./useSpecimens";
import { PHYSICALLY_CONSUMED_SPECIMEN_USAGES } from "./frozenSpecimenUsages";

type TransferEmbryoCandidate = Pick<
  Specimen,
  "id" | "type" | "status" | "specimenUsages"
>;

function usedInCycle(
  specimen: TransferEmbryoCandidate,
  cycleId: string,
): boolean {
  return (specimen.specimenUsages ?? []).some(
    (usage) => usage.cycleId === cycleId && usage.usage === "use",
  );
}

/**
 * The embryos picked for this frozen transfer that it can still account for:
 * picked when the cycle started, in picker order, and not used, discarded or
 * donated outside this cycle.
 */
export function getPickedEmbryoIdsForTransfer(
  specimens: readonly TransferEmbryoCandidate[],
  cycleId: string,
  pickedIds: readonly string[],
): string[] {
  const byId = new Map(specimens.map((specimen) => [specimen.id, specimen]));
  return pickedIds.filter((id) => {
    const specimen = byId.get(id);
    if (!specimen || specimen.type !== "embryo") return false;
    if (specimen.status === "discarded" || specimen.status === "donated") {
      return false;
    }
    if (specimen.status === "used" && !usedInCycle(specimen, cycleId)) {
      return false;
    }
    // Older cycles could release a hold; a released embryo is no longer
    // part of this transfer.
    const releasedHere = (specimen.specimenUsages ?? []).some(
      (usage) => usage.cycleId === cycleId && usage.usage === "release",
    );
    if (releasedHere && !usedInCycle(specimen, cycleId)) return false;
    return !(specimen.specimenUsages ?? []).some(
      (usage) =>
        usage.cycleId !== cycleId &&
        PHYSICALLY_CONSUMED_SPECIMEN_USAGES.has(usage.usage),
    );
  });
}

/**
 * Which picked embryos to mark used once the transfer is saved.
 *
 * Only when the number transferred matches the number picked do we know
 * which embryos went in. Otherwise mark none: she or her clinic records it
 * from the vault. Embryos already marked used for this cycle are skipped, so
 * editing the step later does not write again.
 */
export function getEmbryoIdsToMarkUsed(
  specimens: readonly TransferEmbryoCandidate[],
  cycleId: string,
  pickedEmbryoIds: readonly string[],
  transferredCount: number,
): string[] {
  if (transferredCount <= 0 || transferredCount !== pickedEmbryoIds.length) {
    return [];
  }
  const byId = new Map(specimens.map((specimen) => [specimen.id, specimen]));
  return pickedEmbryoIds.filter((id) => {
    const specimen = byId.get(id);
    return specimen !== undefined && !usedInCycle(specimen, cycleId);
  });
}

/**
 * Whether she still needs to say which picked embryos went in. True when the
 * count does not match the number picked, so nothing was marked, and the
 * embryos already marked used for this cycle do not account for the count
 * yet. Once she records them from the vault, this stops asking.
 */
export function needsTransferredEmbryoChoice(
  specimens: readonly TransferEmbryoCandidate[],
  cycleId: string,
  pickedEmbryoIds: readonly string[],
  transferredCount: number,
): boolean {
  if (transferredCount <= 0 || pickedEmbryoIds.length === 0) return false;
  if (transferredCount === pickedEmbryoIds.length) return false;
  const byId = new Map(specimens.map((specimen) => [specimen.id, specimen]));
  const markedHere = pickedEmbryoIds.filter((id) => {
    const specimen = byId.get(id);
    return specimen !== undefined && usedInCycle(specimen, cycleId);
  }).length;
  return markedHere !== transferredCount;
}
